/**
 * InBodyBackoffice.jsx
 * Vista completa do relatório InBody para o backoffice da nutricionista.
 * Substitui / expande a secção "Evolução > InBody" existente.
 *
 * Props:
 *   data — objecto devolvido pelo parse_inbody_csv (ou endpoint /inbody)
 *   patientName — string com o nome da cliente
 */

import { useState } from "react";
import { NutriTooltip, NUTRI_TOOLTIPS } from "./InBodyTooltips";

// ---------------------------------------------------------------------------
// Dados de demonstração (ficheiro cn002)
// ---------------------------------------------------------------------------
const DEMO = {
  name: "Cristina Paula Garcia de Magalhães",
  test_datetime: "2024-11-29T00:00:00",
  height_cm: 160,
  age: 59,
  gender: "F",
  weight_kg: 60.8,
  bfm_kg: 22.3,
  ffm_kg: 38.5,
  smm_kg: 20.9,
  tbw_l: 28.2,
  protein_kg: 7.5,
  minerals_kg: 2.76,
  pbf: 36.6,
  bmi: 23.8,
  inbody_score: 67,
  vfl: 11,
  obesity_degree: 110,
  whr: 0.92,
  bmr_kcal: 1203,
  recommended_calories: 1385,
  smi: 6.0,
  ffmi: 15.0,
  fmi: 8.7,
  abdomen_circumference_cm: 86,
  control_targets: {
    target_weight_kg: 55.0,
    weight_control_kg: -5.8,
    bfm_control_kg: -9.6,
    ffm_control_kg: 3.8,
  },
  segments: {
    lean_mass: {
      right_arm: { kg: 1.91, pct: 92.1 },
      left_arm:  { kg: 1.82, pct: 87.8 },
      trunk:     { kg: 17.7, pct: 94.1 },
      right_leg: { kg: 5.83, pct: 88.9 },
      left_leg:  { kg: 5.87, pct: 89.5 },
    },
    fat_mass: {
      right_arm: { kg: 1.6,  pct: 169.5 },
      left_arm:  { kg: 1.6,  pct: 174.5 },
      trunk:     { kg: 11.3, pct: 218.6 },
      right_leg: { kg: 3.3,  pct: 141.6 },
      left_leg:  { kg: 3.3,  pct: 141.6 },
    },
  },
  reference_ranges: {
    weight:   { min: 46.8, max: 63.3 },
    bfm:      { min: 11.0, max: 17.6 },
    ffm:      { min: 38.1, max: 46.6 },
    smm:      { min: 20.9, max: 25.5 },
    tbw:      { min: 28.0, max: 34.2 },
    protein:  { min: 7.5,  max: 9.1  },
    minerals: { min: 2.59, max: 3.17 },
    bmi:      { min: 18.5, max: 25.0 },
    pbf:      { min: 18.0, max: 28.0 },
    bmr:      { min: 1280, max: 1482 },
    whr:      { min: 0.75, max: 0.85 },
  },
};

// ---------------------------------------------------------------------------
// Paleta e helpers
// ---------------------------------------------------------------------------
const COLORS = {
  sage:       "#5C7A5E",
  sageLight:  "#E8F0E9",
  sageMid:    "#A8C2AA",
  amber:      "#C97D2E",
  amberLight: "#FAF0E4",
  red:        "#B84040",
  redLight:   "#FAEAEA",
  neutral:    "#F5F4F1",
  text:       "#1E2420",
  muted:      "#6B7570",
  border:     "#DDD9D2",
  white:      "#FFFFFF",
};

const fmt = (n, dec = 1) => n != null ? Number(n).toFixed(dec) : "-";

function statusFor(value, range) {
  if (!range || value == null) return "normal";
  if (value < range.min) return "low";
  if (value > range.max) return "high";
  return "normal";
}

function StatusDot({ status }) {
  const colors = { normal: COLORS.sage, high: COLORS.amber, low: "#5B8FC9" };
  return (
    <span style={{
      display: "inline-block", width: 8, height: 8,
      borderRadius: "50%", background: colors[status] || COLORS.sage,
      flexShrink: 0,
    }} />
  );
}

// ---------------------------------------------------------------------------
// Barra de intervalo de referência
// ---------------------------------------------------------------------------
function RangeBar({ value, min, max, unit = "", decimals = 1 }) {
  if (value == null || min == null || max == null) return null;
  const span = max - min;
  const extended = span * 0.4;
  const domainMin = Math.max(0, min - extended);
  const domainMax = max + extended;
  const domain = domainMax - domainMin;
  const pct = (v) => Math.min(100, Math.max(0, ((v - domainMin) / domain) * 100));
  const valPct = pct(value);
  const minPct = pct(min);
  const maxPct = pct(max);
  const status = statusFor(value, { min, max });
  const markerColor = status === "normal" ? COLORS.sage : status === "high" ? COLORS.amber : "#5B8FC9";

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ position: "relative", height: 6, borderRadius: 4, background: COLORS.border }}>
        {/* zona normal */}
        <div style={{
          position: "absolute", height: "100%", borderRadius: 4,
          left: `${minPct}%`, width: `${maxPct - minPct}%`,
          background: COLORS.sageMid, opacity: 0.5,
        }} />
        {/* marcador do valor */}
        <div style={{
          position: "absolute", top: -3, width: 12, height: 12,
          borderRadius: "50%", background: markerColor,
          border: `2px solid ${COLORS.white}`,
          transform: "translateX(-50%)",
          left: `${valPct}%`,
          boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
        }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: COLORS.muted, marginTop: 3 }}>
        <span>{fmt(min, decimals)}</span>
        <span style={{ fontWeight: 500, color: markerColor }}>{fmt(value, decimals)} {unit}</span>
        <span>{fmt(max, decimals)}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cartão de métrica individual
// ---------------------------------------------------------------------------
function MetricCard({ label, value, unit, decimals = 1, range, rangeUnit, children, tooltipKey, delta }) {
  const status = range ? statusFor(value, range) : "normal";
  const statusLabel = { normal: "Normal", high: "Acima", low: "Abaixo" };
  const statusBg = { normal: COLORS.sageLight, high: COLORS.amberLight, low: "#EAF1FA" };
  const statusColor = { normal: COLORS.sage, high: COLORS.amber, low: "#5B8FC9" };

  return (
    <div style={{
      background: COLORS.white, border: `1px solid ${COLORS.border}`,
      borderRadius: 12, padding: "16px 18px",
    }}>
      <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 4, fontWeight: 500, letterSpacing: "0.02em", display: "flex", alignItems: "center" }}>
        {label}
        {tooltipKey && NUTRI_TOOLTIPS[tooltipKey] && <NutriTooltip text={NUTRI_TOOLTIPS[tooltipKey]} />}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: range ? 2 : 0 }}>
        <span style={{ fontSize: 26, fontWeight: 600, color: COLORS.text, letterSpacing: "-0.02em" }}>
          {value != null ? fmt(value, decimals) : "-"}
        </span>
        {unit && <span style={{ fontSize: 14, color: COLORS.muted }}>{unit}</span>}
        {delta && <span style={{ fontSize: 11, fontWeight: 600, color: delta.color, marginLeft: 6 }}>{delta.text}</span>}
        {range && (
          <span style={{
            marginLeft: "auto", fontSize: 11, fontWeight: 600,
            padding: "2px 8px", borderRadius: 20,
            background: statusBg[status], color: statusColor[status],
          }}>
            {statusLabel[status]}
          </span>
        )}
      </div>
      {range && (
        <RangeBar value={value} min={range.min} max={range.max} unit={rangeUnit || unit} decimals={decimals} />
      )}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Secção de segmentação corporal
// ---------------------------------------------------------------------------
const SEG_LABELS = {
  right_arm: "Braço D.",
  left_arm:  "Braço E.",
  trunk:     "Tronco",
  right_leg: "Perna D.",
  left_leg:  "Perna E.",
};

function SegmentRow({ label, lean, fat, leanDelta, fatDelta }) {
  const maxPct = 250;
  const leanW = Math.min(100, (lean.pct / 100) * 100);
  const fatW  = Math.min(100, (fat.pct  / maxPct) * 100);
  const leanOk = lean.pct >= 80 && lean.pct <= 120;
  const fatOk  = fat.pct  <= 100;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr", gap: 12, alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}>
      <span style={{ fontSize: 13, fontWeight: 500, color: COLORS.text }}>{label}</span>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
          <span style={{ fontSize: 11, color: COLORS.muted }}>Massa magra</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: leanOk ? COLORS.sage : COLORS.amber }}>
            {fmt(lean.kg)} kg · {lean.pct}%
            {leanDelta && <span style={{ fontSize: 10, color: leanDelta.color, marginLeft: 4 }}>{leanDelta.text}</span>}
          </span>
        </div>
        <div style={{ height: 6, borderRadius: 3, background: COLORS.border }}>
          <div style={{
            height: "100%", borderRadius: 3, width: `${leanW}%`,
            background: leanOk ? COLORS.sage : COLORS.amber,
            transition: "width 0.6s ease",
          }} />
        </div>
      </div>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
          <span style={{ fontSize: 11, color: COLORS.muted }}>Massa gorda</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: fatOk ? COLORS.sage : COLORS.red }}>
            {fmt(fat.kg)} kg · {fat.pct}%
            {fatDelta && <span style={{ fontSize: 10, color: fatDelta.color, marginLeft: 4 }}>{fatDelta.text}</span>}
          </span>
        </div>
        <div style={{ height: 6, borderRadius: 3, background: COLORS.border }}>
          <div style={{
            height: "100%", borderRadius: 3, width: `${fatW}%`,
            background: fatOk ? COLORS.sageMid : COLORS.red,
            opacity: 0.8,
            transition: "width 0.6s ease",
          }} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gráfico de composição em pilha horizontal
// ---------------------------------------------------------------------------
function CompositionStack({ data }) {
  const total = data.weight_kg;
  const bfm   = data.bfm_kg;
  const smm   = data.smm_kg;
  const other = total - bfm - smm;

  const segments = [
    { label: "Massa gorda",    value: bfm,   color: COLORS.amber,   pct: (bfm / total * 100).toFixed(1) },
    { label: "Massa muscular", value: smm,   color: COLORS.sage,    pct: (smm / total * 100).toFixed(1) },
    { label: "Outros",         value: other, color: COLORS.border,  pct: (other / total * 100).toFixed(1) },
  ];

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", height: 28, borderRadius: 6, overflow: "hidden", gap: 2 }}>
        {segments.map((s) => (
          <div key={s.label} title={`${s.label}: ${fmt(s.value)} kg`} style={{
            flex: s.value, background: s.color, transition: "flex 0.6s ease",
          }} />
        ))}
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 10 }}>
        {segments.map((s) => (
          <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: COLORS.muted }}>{s.label}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{s.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Painel de objetivos de controlo
// ---------------------------------------------------------------------------
function ControlTargets({ targets }) {
  const items = [
    { label: "Peso alvo",           value: targets.target_weight_kg,  unit: "kg",  sign: false },
    { label: "Ajuste de peso",      value: targets.weight_control_kg, unit: "kg",  sign: true  },
    { label: "Reduzir gordura",     value: targets.bfm_control_kg,    unit: "kg",  sign: true  },
    { label: "Ganhar músculo",      value: targets.ffm_control_kg,    unit: "kg",  sign: true  },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
      {items.map((item) => {
        const isNeg = item.value < 0;
        const color = item.sign ? (isNeg ? COLORS.red : COLORS.sage) : COLORS.text;
        return (
          <div key={item.label} style={{
            background: COLORS.neutral, borderRadius: 10, padding: "12px 14px",
          }}>
            <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 4, fontWeight: 500 }}>{item.label}</div>
            <div style={{ fontSize: 20, fontWeight: 600, color, letterSpacing: "-0.02em" }}>
              {item.sign && item.value > 0 ? "+" : ""}{fmt(item.value)} {item.unit}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
export default function InBodyBackoffice({ data = DEMO, patientName, allRecords = [], currentDate = '' }) {
  const [tab, setTab] = useState("composicao");
  const [compareMode, setCompareMode] = useState("previous"); // previous | first | custom
  const [customDate, setCustomDate] = useState("");
  const d = data;
  const r = d.reference_ranges || {};
  const segs = d.segments || {};
  const date = d.test_datetime
    ? new Date(d.test_datetime).toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" })
    : "-";

  // Sort records chronologically and find comparison record
  const sorted = [...allRecords].sort((a, b) => a.date.localeCompare(b.date));
  const currentIdx = sorted.findIndex(r => r.date === currentDate);
  const hasMultiple = sorted.length > 1;

  let refRecord = null;
  if (hasMultiple) {
    if (compareMode === "previous" && currentIdx > 0) refRecord = sorted[currentIdx - 1]?.data;
    else if (compareMode === "first" && currentIdx > 0) refRecord = sorted[0]?.data;
    else if (compareMode === "custom" && customDate) refRecord = sorted.find(r => r.date === customDate)?.data;
  }

  // Delta calculation with clinical color
  const DELTA_RULES = {
    weight_kg: "decrease", bfm_kg: "decrease", pbf: "decrease", ffm_kg: "increase",
    smm_kg: "increase", tbw_l: "increase", protein_kg: "neutral", minerals_kg: "neutral",
    bmi: "decrease", vfl: "decrease", inbody_score: "increase", obesity_degree: "decrease",
    smi: "increase", ffmi: "increase", fmi: "decrease", whr: "decrease",
    abdomen_circumference_cm: "decrease", bmr_kcal: "neutral", smm_weight_ratio: "increase",
  };
  const getDelta = (field, decimals = 1) => {
    if (!refRecord || d[field] == null || refRecord[field] == null) return null;
    const diff = d[field] - refRecord[field];
    if (Math.abs(diff) < 0.01) return { value: 0, text: "0", color: COLORS.muted };
    const rule = DELTA_RULES[field] || "neutral";
    let color = COLORS.muted;
    if (rule === "increase") color = diff > 0 ? COLORS.sage : COLORS.red;
    else if (rule === "decrease") color = diff < 0 ? COLORS.sage : COLORS.red;
    return { value: diff, text: `${diff > 0 ? "+" : ""}${diff.toFixed(decimals)}`, color };
  };
  const getSegDelta = (type, key) => {
    if (!refRecord) return null;
    const curr = d.segments?.[type]?.[key]?.kg;
    const prev = refRecord.segments?.[type]?.[key]?.kg;
    if (curr == null || prev == null) return null;
    const diff = curr - prev;
    if (Math.abs(diff) < 0.01) return null;
    const rule = type === "lean_mass" ? "increase" : "decrease";
    const color = rule === "increase" ? (diff > 0 ? COLORS.sage : COLORS.red) : (diff < 0 ? COLORS.sage : COLORS.red);
    return { text: `${diff > 0 ? "+" : ""}${diff.toFixed(1)}`, color };
  };

  // Dates available for custom picker
  const availableDates = sorted.filter(r => r.date !== currentDate).map(r => r.date);

  const tabs = [
    { id: "composicao", label: "Composição" },
    { id: "segmentos",  label: "Segmentos" },
    { id: "indices",    label: "Índices" },
    { id: "objetivos",  label: "Objetivos" },
  ];

  // Muscle body figure SVG
  const MuscleFigure = () => {
    const lm = segs.lean_mass || {};
    const getIntensity = (key) => { const pct = lm[key]?.pct || 100; return Math.min(1, Math.max(0.3, pct / 120)); };
    const getColor = (key) => { const i = getIntensity(key); return `rgba(101,113,97,${i})`; };
    return (
      <div style={{ textAlign: "center" }}>
        <svg viewBox="0 0 200 340" width="140" style={{ margin: "0 auto" }}>
          {/* Head */}
          <circle cx="100" cy="30" r="22" fill="#E8EBE6" />
          {/* Trunk */}
          <rect x="70" y="55" width="60" height="100" rx="12" fill={getColor("trunk")} />
          {/* Right arm */}
          <rect x="38" y="60" width="28" height="80" rx="10" fill={getColor("right_arm")} />
          {/* Left arm */}
          <rect x="134" y="60" width="28" height="80" rx="10" fill={getColor("left_arm")} />
          {/* Right leg */}
          <rect x="68" y="160" width="28" height="110" rx="10" fill={getColor("right_leg")} />
          {/* Left leg */}
          <rect x="104" y="160" width="28" height="110" rx="10" fill={getColor("left_leg")} />
        </svg>
        <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 8, fontSize: 10, color: COLORS.muted }}>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: "rgba(101,113,97,0.9)" }} />Mais músculo</span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: "rgba(101,113,97,0.4)" }} />Normal</span>
        </div>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif", color: COLORS.text, maxWidth: 860, margin: "0 auto", padding: "0 0 40px" }}>
      {/* Cabeçalho */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "16px 0 12px", borderBottom: `1px solid ${COLORS.border}`,
        marginBottom: 20,
      }}>
        <div>
          <div style={{ fontSize: 11, color: COLORS.muted, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 3 }}>
            InBody · {date}
          </div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>{d.name || "Cristina Paula Garcia de Magalhães"}</div>
          <div style={{ fontSize: 13, color: COLORS.muted, marginTop: 2 }}>
            {d.height_cm} cm · {d.age} anos · {d.gender === "F" ? "Feminino" : "Masculino"}
          </div>
        </div>
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          background: d.inbody_score >= 70 ? COLORS.sageLight : COLORS.amberLight,
          borderRadius: 14, padding: "14px 20px", gap: 2,
        }}>
          <span style={{ fontSize: 34, fontWeight: 700, color: d.inbody_score >= 70 ? COLORS.sage : COLORS.amber, lineHeight: 1 }}>
            {d.inbody_score}
          </span>
          {getDelta("inbody_score", 0) && <span style={{ fontSize: 11, fontWeight: 600, color: getDelta("inbody_score", 0).color }}>{getDelta("inbody_score", 0).text}</span>}
          <span style={{ fontSize: 11, color: COLORS.muted, fontWeight: 500, display: "flex", alignItems: "center" }}>InBody Score<NutriTooltip text={NUTRI_TOOLTIPS.inbody_score} /></span>
        </div>
      </div>

      {/* Comparison selector */}
      {hasMultiple && (
        <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: COLORS.muted, fontWeight: 500 }}>Comparar com:</span>
          {["previous", "first", "custom"].map(m => (
            <button key={m} onClick={() => setCompareMode(m)} style={{
              padding: "4px 12px", borderRadius: 20, border: "none", cursor: "pointer",
              fontSize: 11, fontWeight: 500,
              background: compareMode === m ? COLORS.sage : COLORS.neutral,
              color: compareMode === m ? COLORS.white : COLORS.muted,
            }}>
              {m === "previous" ? "Anterior" : m === "first" ? "Primeira" : "Data"}
            </button>
          ))}
          {compareMode === "custom" && (
            <select value={customDate} onChange={e => setCustomDate(e.target.value)} style={{ fontSize: 11, padding: "4px 8px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.text }}>
              <option value="">Selecionar...</option>
              {availableDates.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          {refRecord && <span style={{ fontSize: 10, color: COLORS.sage }}>A comparar</span>}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, background: COLORS.neutral, borderRadius: 10, padding: 4 }}>
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, padding: "8px 0", border: "none", cursor: "pointer",
            borderRadius: 7, fontSize: 13, fontWeight: 500,
            background: tab === t.id ? COLORS.white : "transparent",
            color: tab === t.id ? COLORS.text : COLORS.muted,
            boxShadow: tab === t.id ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
            transition: "all 0.15s",
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Conteúdo das tabs */}
      {tab === "composicao" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Stack de composição */}
          <div style={{ background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "16px 18px" }}>
            <div style={{ fontSize: 12, color: COLORS.muted, fontWeight: 500, marginBottom: 2 }}>Composição corporal</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <span style={{ fontSize: 26, fontWeight: 600, letterSpacing: "-0.02em" }}>{fmt(d.weight_kg)} kg</span>
              {getDelta("weight_kg", 1) && <span style={{ fontSize: 12, fontWeight: 600, color: getDelta("weight_kg", 1).color }}>{getDelta("weight_kg", 1).text} kg</span>}
            </div>
            <CompositionStack data={d} />
          </div>

          {/* Grid de métricas */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            <MetricCard label="Massa gorda" value={d.bfm_kg} unit="kg" range={r.bfm} tooltipKey="massa_gorda" delta={getDelta("bfm_kg")} />
            <MetricCard label="Massa magra" value={d.ffm_kg} unit="kg" range={r.ffm} delta={getDelta("ffm_kg")} />
            <MetricCard label="Massa muscular" value={d.smm_kg} unit="kg" range={r.smm} tooltipKey="massa_muscular" delta={getDelta("smm_kg")} />
            <MetricCard label="Água corporal" value={d.tbw_l} unit="L" range={r.tbw} tooltipKey="agua" delta={getDelta("tbw_l")} />
            <MetricCard label="Proteína" value={d.protein_kg} unit="kg" range={r.protein} delta={getDelta("protein_kg")} />
            <MetricCard label="Minerais" value={d.minerals_kg} unit="kg" decimals={2} range={r.minerals} delta={getDelta("minerals_kg", 2)} />
          </div>
        </div>
      )}

      {tab === "segmentos" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Body figures side by side */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {/* Existing fat body figure placeholder */}
            <div style={{ background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 16, textAlign: "center" }}>
              <div style={{ fontSize: 11, color: COLORS.muted, fontWeight: 500, marginBottom: 8 }}>Gordura por zona</div>
              <svg viewBox="0 0 200 340" width="120" style={{ margin: "0 auto" }}>
                <circle cx="100" cy="30" r="22" fill="#E8EBE6" />
                <rect x="70" y="55" width="60" height="100" rx="12" fill={`rgba(217,163,80,${Math.min(1, (segs.fat_mass?.trunk?.pct || 100) / 150)})`} />
                <rect x="38" y="60" width="28" height="80" rx="10" fill={`rgba(217,163,80,${Math.min(1, (segs.fat_mass?.right_arm?.pct || 100) / 150)})`} />
                <rect x="134" y="60" width="28" height="80" rx="10" fill={`rgba(217,163,80,${Math.min(1, (segs.fat_mass?.left_arm?.pct || 100) / 150)})`} />
                <rect x="68" y="160" width="28" height="110" rx="10" fill={`rgba(217,163,80,${Math.min(1, (segs.fat_mass?.right_leg?.pct || 100) / 150)})`} />
                <rect x="104" y="160" width="28" height="110" rx="10" fill={`rgba(217,163,80,${Math.min(1, (segs.fat_mass?.left_leg?.pct || 100) / 150)})`} />
              </svg>
              <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 8, fontSize: 10, color: COLORS.muted }}>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: "rgba(217,163,80,0.9)" }} />Mais gordura</span>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: "rgba(217,163,80,0.3)" }} />Normal</span>
              </div>
            </div>
            {/* Muscle body figure */}
            <div style={{ background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 16 }}>
              <div style={{ fontSize: 11, color: COLORS.muted, fontWeight: 500, marginBottom: 8, textAlign: "center" }}>Músculo por zona</div>
              <MuscleFigure />
            </div>
          </div>
          {/* Segment bars with deltas */}
          <div style={{ background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "16px 18px" }}>
            <div style={{ fontSize: 12, color: COLORS.muted, fontWeight: 500, marginBottom: 12 }}>
              Distribuição por segmento · 100% = valor de referência
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr", gap: 12, paddingBottom: 8, borderBottom: `1px solid ${COLORS.border}`, marginBottom: 4 }}>
              <span />
              <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.muted }}>MASSA MAGRA</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.muted }}>MASSA GORDA</span>
            </div>
            {Object.entries(SEG_LABELS).map(([key, label]) => (
              <SegmentRow
                key={key}
                label={label}
                lean={segs.lean_mass?.[key] || { kg: 0, pct: 0 }}
                fat={segs.fat_mass?.[key]  || { kg: 0, pct: 0 }}
                leanDelta={getSegDelta("lean_mass", key)}
                fatDelta={getSegDelta("fat_mass", key)}
              />
            ))}
          </div>
        </div>
      )}

      {tab === "indices" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          <MetricCard label="IMC" value={d.bmi} unit="" decimals={1} range={r.bmi} tooltipKey="imc" delta={getDelta("bmi", 1)} />
          <MetricCard label="% Gordura" value={d.pbf} unit="%" range={r.pbf} tooltipKey="massa_gorda" delta={getDelta("pbf", 1)} />
          <MetricCard label="Gordura visceral" value={d.vfl} unit="nível" decimals={0}
            range={{ min: 1, max: 9 }} tooltipKey="gordura_visceral" delta={getDelta("vfl", 0)} />
          <MetricCard label="Rácio cintura/anca" value={d.whr} unit="" decimals={2} range={r.whr} delta={getDelta("whr", 2)} />
          <MetricCard label="Grau de obesidade" value={d.obesity_degree} unit="%" decimals={0}
            range={{ min: 80, max: 100 }} tooltipKey="grau_obesidade" delta={getDelta("obesity_degree", 0)} />
          <MetricCard label="Índice muscular (SMI)" value={d.smi} unit="kg/m²" decimals={1} tooltipKey="smi" delta={getDelta("smi", 1)} />
          <MetricCard label="FFMI" value={d.ffmi} unit="" decimals={1} tooltipKey="ffmi" delta={getDelta("ffmi", 1)} />
          <MetricCard label="FMI" value={d.fmi} unit="" decimals={1} tooltipKey="fmi" delta={getDelta("fmi", 1)} />
          <MetricCard label="Circunf. abdominal" value={d.abdomen_circumference_cm} unit="cm" decimals={0} tooltipKey="circunferencia_abdominal" delta={getDelta("abdomen_circumference_cm", 0)} />
          <MetricCard label="TMB" value={d.bmr_kcal} unit="kcal" decimals={0} range={r.bmr} rangeUnit="kcal" delta={getDelta("bmr_kcal", 0)} />
          <MetricCard label="Calorias recomendadas" value={d.recommended_calories} unit="kcal" decimals={0} />
          <MetricCard label="Rácio músculo/peso" value={d.smm_weight_ratio} unit="%" decimals={1} tooltipKey="racio_musculo_peso" delta={getDelta("smm_weight_ratio", 1)} />
        </div>
      )}

      {tab === "objetivos" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ fontSize: 13, color: COLORS.muted, padding: "12px 16px", background: COLORS.sageLight, borderRadius: 10, lineHeight: 1.6 }}>
            Objetivos calculados pelo InBody com base no peso e composição actuais.
          </div>
          <ControlTargets targets={d.control_targets || {}} />
        </div>
      )}
    </div>
  );
}
